import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, model, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CardGroupComponent,
  EnumHelper,
  InputErrorsComponent,
  MbsPopupIcon,
  MbsPopupType,
  MbsSize,
  NumberComponent,
  SafePipe,
  SelectComponent,
  SwitcherComponent
} from 'mbs-ui-kit';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { Period, PeriodDisplay } from 'mbs-ui-shared@models/Period.enum';
import { startWith } from 'rxjs';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import {
  CalculatedPropertyRetention,
  CalculatedPropertySchedule,
  CalculatedPropertyStorage,
  WizardStepsService
} from 'wizards/services/wizard-steps.service';

@UntilDestroy()
@Component({
  selector: 'mbs-nbf-retention-policy-step-control-panel',
  templateUrl: './retention-policy-control-panel.component.html',
  styleUrls: ['./retention-policy-control-panel.component.scss'],
  imports: [
    CommonModule,
    I18NextPipe,
    AlertComponent,
    InputErrorsComponent,
    NumberComponent,
    ReactiveFormsModule,
    SelectComponent,
    SwitcherComponent,
    NgbTooltipModule,
    HelpLinkDirective,
    ButtonComponent,
    CardGroupComponent,
    SafePipe
  ]
})
export class RetentionPolicyControlPanelComponent {
  protected readonly MbsSize = MbsSize;
  protected readonly MbsPopupIcon = MbsPopupIcon;
  protected readonly MbsPopupType = MbsPopupType;

  private stepService = inject(WizardStepsService);

  // Backup Templates wizards (company wizard templates and remote deploy configurations) have no computer behind them, and the
  // Intelligent Retention alert is not shown there
  protected readonly isTemplateWizard = !inject(RemoteManagementWizardsService).hid;

  parentForm = input<FormGroup>();
  layout = input<'standard' | 'compact'>('standard');
  mode = input<'manual' | 'simple' | 'advanced' | 'alert' | 'hybrid'>();
  placement = input<'retention' | 'schedule'>('retention');
  GFSInfo = input<Array<string>>();
  forceGFSShow = input<boolean>();
  isHybrid = input<boolean>(false);
  isWindows = input<boolean>(false);
  schedule = model<Partial<CalculatedPropertySchedule>>();
  storage = model<Partial<CalculatedPropertyStorage>>();
  alerts = model<CalculatedPropertyRetention['alerts']>();

  internalClick = output<'openRetention'>();
  clickDetailsGFS = output<void>();
  retentionChange = output<void>();
  retentionHybridChange = output<void>();
  GFSEnabledChange = output<void>();
  beforeGFSEnabledChange = output<{ currentValue: boolean; nextValue: boolean; stop: () => void }>();
  retentionUseDefaultSettingsChange = output<void>();
  deleteVersionsOlderThanHybridChange = output<boolean>();

  public periodEnumSelectArray = EnumHelper.EnumToSelectIndexesArray(Period, PeriodDisplay);
  public elementSelectors = {
    id: {
      notScheduleAlert: 'NBF-Retention-not-full-alert',
      simpleInfo: 'NBF-Retention-simple-info',
      simpleBlock: 'NBF-Retention-simple-block',
      simpleBlockCount: 'NBF-Retention-simple-block-count',
      simpleBlockPeriod: 'NBF-Retention-simple-block-period',
      simpleBlockSwitcher: 'NBF-Retention-simple-block-switcher',
      simpleBlockMinPeriodAlert: 'NBF-Retention-simple-block-min-period-alert',
      simpleBlockRecommendTurnOn: 'NBF-Retention-simple-block-recommend-turn-on',
      advancedInfo: 'NBF-Retention-advanced-info',
      advancedInfoIncrementalSchedule: 'NBF-Retention-advanced-info-incremental-schedule',
      advancedInfoNoIncrementalSchedule: 'NBF-Retention-advanced-info-no-incremental-schedule',
      advancedBlock: 'NBF-Retention-advanced-block',
      advancedBlockMinPeriodAlert: 'NBF-Retention-advanced-block-min-period-alert',
      needRetentionForGFSAlert: 'NBF-Retention-need-retention-for-GFS-alert',
      switcherGFS: 'NBF-Retention-switcher-GFS',
      buttonGFS: 'NBF-Retention-button-GFS',
      storageBlock: 'NBF-Retention-storage-block',
      storageHasObjectLock: 'NBF-Retention-storage-object-lock',
      alertExpandButton: 'NBF-Retention-alert-expand-button',
      GFSBlock: 'NBF-Retention-GFS-block'
    }
  };

  protected isManualMode = model<boolean>();
  protected isSimpleMode = model<boolean>();
  protected isAdvancedMode = model<boolean>();
  protected isAlertMode = model<boolean>();
  protected isHybridMode = model<boolean>();

  protected alertsExpanded = model<boolean>(true);
  protected alertsCount = computed<{ danger: number; warning: number; info: number; total: number }>(() => {
    const result = { danger: 0, warning: 0, info: 0 };
    const alerts = this.alerts();
    const storage = this.storage();
    const parentForm = this.parentForm();
    const isAdvancedMode = this.isAdvancedMode();
    const isSimpleMode = this.isSimpleMode();

    if (isSimpleMode && storage?.minPeriod && !this.isTemplateWizard) {
      result.info++;
    }

    if (isAdvancedMode && alerts?.needMinPeriodAlert) {
      result.info++;
    }

    if (parentForm.get('RetentionUseDefaultSettings')?.value && !alerts?.needRetentionForGFSAlert) {
      result.danger++;
    }

    if (!parentForm.get('RetentionUseDefaultSettings')?.value && alerts?.keepBackupFullAlert) {
      result.warning++;
    }

    if (alerts?.needRetentionForGFSAlert) {
      result.danger++;
    }

    return { ...result, total: result.danger + result.warning + result.info };
  });
  protected isCompactLayout = computed(() => this.layout() === 'compact');

  constructor() {
    effect(() => {
      if (this.mode() && this.mode() !== 'alert') {
        this.isManualMode.set(this.mode() === 'manual');
        this.isSimpleMode.set(this.mode() === 'simple');
        this.isAdvancedMode.set(this.mode() === 'advanced');
      } else {
        this.isManualMode.set(this.schedule().isManualMode);
        this.isSimpleMode.set(this.schedule().isSimpleMode);
        this.isAdvancedMode.set(this.schedule().isAdvancedMode);
      }

      this.isAlertMode.set(this.mode() === 'alert');
      this.isHybridMode.set(this.mode() === 'hybrid');
    });

    if (!this.schedule()) {
      this.stepService
        .pipeProperty('schedule')
        .pipe(startWith(this.stepService.getSnapshot('schedule')), untilDestroyed(this))
        .subscribe((data) => this.schedule.set(data));
    }

    if (!this.storage()) {
      this.stepService
        .pipeProperty('storage')
        .pipe(startWith(this.stepService.getSnapshot('storage')), untilDestroyed(this))
        .subscribe((data) => this.storage.set(data));
    }

    if (!this.alerts()) {
      this.stepService
        .pipeProperty('retention')
        .pipe(startWith(this.stepService.getSnapshot('retention')), untilDestroyed(this))
        .subscribe((data) => this.alerts.set(data.alerts));
    }
  }

  public hubridRetentionSwitchHandler(state: boolean): void {
    this.deleteVersionsOlderThanHybridChange.emit(state);
    this.retentionHybridChange.emit();
  }

  public alertClickHandler(event: PointerEvent): void {
    if ((event.target as unknown as HTMLElement).classList.contains('special-action-open-retention')) {
      this.internalClick.emit('openRetention');
    }
  }
}
